import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useMutableValue from './useMutableValue';
import { isSSR } from '../helpers/supports';
import dataTypeName from '@polestar/component-warehouse-react/dataTypeName';
// used to identify the PolestarApp component
const designSystemSelector = `[data-version][data-type="${dataTypeName}"]`;
/**
 * Set the CSS Custom property value of a provided name (on a specific node if needed) like:
 *
 * ```js
 * import { setCSSCustomProp } from '@polestar/component-warehouse-react';
 * setCSSCustomProp('--test-var', 'value);
 * ```
 */
export const setCSSCustomProp = (propName, value, node) => {
    var _a;
    if (isSSR) {
        return;
    }
    if (node === undefined) {
        node = document.querySelector(designSystemSelector);
    }
    (_a = node === null || node === void 0 ? void 0 : node.style) === null || _a === void 0 ? void 0 : _a.setProperty(propName, value);
};
/**
 * Get the CSS Custom property value of a provided name. Default it will get the property definition at <PolestarApp/> but can be adapted to a specific DOM element like:
 *
 * ```js
 * import { getCSSCustomProp } from '@polestar/component-warehouse-react';
 * const value  = getCSSCustomProp('--test-var');
 * ```
 */
export const getCSSCustomProp = (propName, node) => {
    if (typeof window === 'undefined') {
        // cannot support SSR while it needs the DOM to get the value
        return undefined;
    }
    // if not looking for a specific node then fallback to the PolestarApp
    if (node === undefined) {
        node = document.querySelector(designSystemSelector);
    }
    if (node === null) {
        return undefined;
    }
    return getComputedStyle(node).getPropertyValue(propName);
};
/**
 * Listen to changes in a CSS Custom Property like:
 *
 * ```js
 * import { useCSSCustomPropWatch } from '@polestar/component-warehouse-react';
 * const cssCustomProp = useCSSCustomPropWatch('div[data-version]', '--test-var');
 * useEffect(() => {
 *   cssCustomProp.on('change', (val) => {
 *     // use a setXXX(val) to trigger a re-render
 *   });
 *   return () => {
 *     // cleanup
 *     cssCustomProp.destroy();
 *   };
 * }, [cssCustomProp]);
 * ```
 *
 * @param {HTMLElement|string} node - Be mindful of providing DOM elements that might not be accessible when initially executed then use the node as a querySelector string.
 * @param {string} cssCustomProp - The CSS Custom Property to search for mutations.
 *
 * @storybook Hooks/useCSSCustomPropWatch
 */
export const useCSSCustomPropWatch = (node, cssCustomProp) => {
    const cssValue = useMutableValue(undefined);
    const observer = useRef(null);
    const config = useMemo(() => ({ attributes: true, childList: true, subtree: true }), []);
    const [domElement, setDOMElement] = useState(typeof node !== 'string' ? node : undefined);
    const callback = useCallback((mutationList) => {
        for (const mutation of mutationList) {
            if (mutation.attributeName === 'style' || mutation.attributeName === 'data-resizing') {
                const value = getCSSCustomProp(cssCustomProp, domElement);
                if (value !== cssValue.get() &&
                    // wait until defined
                    value !== '') {
                    cssValue.set(value);
                }
            }
        }
    }, [domElement, cssCustomProp, cssValue]);
    useEffect(() => {
        if (typeof node === 'string') {
            // wait until available
            const waitForElement = (selector, callback) => {
                const check = () => {
                    const element = document.querySelector(selector);
                    if (element) {
                        // available
                        callback(element);
                    }
                    else {
                        window.requestAnimationFrame(check);
                    }
                };
                check();
            };
            waitForElement(node, (element) => {
                // Element-specific code here
                setDOMElement(element);
            });
        }
    }, [node]);
    useEffect(() => {
        observer.current = new MutationObserver(callback);
        if (domElement) {
            observer.current.observe(domElement, config);
        }
        return () => {
            var _a;
            // cleanup
            (_a = observer.current) === null || _a === void 0 ? void 0 : _a.disconnect();
        };
    }, [callback, domElement, config, cssValue]);
    useEffect(() => {
        // get initial value only
        if (domElement) {
            const value = getCSSCustomProp(cssCustomProp, domElement);
            // wait until defined
            if (value !== '') {
                cssValue.set(value);
            }
        }
    }, [cssCustomProp, domElement, cssValue]);
    return cssValue;
};
